import React, { createContext, useContext, useState, useEffect } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'

type AuthContextType = {
  user: User | null
  session: Session | null
  loading: boolean
  companyId: string | null
  login: (email: string, password: string) => Promise<{ error?: string }>
  logout: () => Promise<void>
  updatePassword: (currentPassword: string, newPassword: string) => Promise<{ error?: string }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [companyId, setCompanyId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      setSession(session)
      setUser(session?.user ?? null)

      if (session?.user?.id) {
        fetchCompanyId(session.user.id)
      } else {
        // Oturum kapandıysa companyId'yi temizle
        setCompanyId(null)
      }

      setLoading(false)
    }

    getInitialSession()

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session)
      setUser(session?.user ?? null)

      if (session?.user?.id) {
        fetchCompanyId(session.user.id)
      } else {
        // Oturum kapandıysa companyId'yi temizle
        setCompanyId(null)
      }

      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  const fetchCompanyId = async (userId: string) => {
    const { data, error } = await supabase
      .from('users')
      .select('company_id')
      .eq('id', userId)
      .single()

    if (error) {
      console.error('Error fetching company_id:', error)
      setCompanyId(null)
    } else {
      //@ts-ignore
      setCompanyId(data.company_id ?? null)
    }
  }

  const login = async (email: string, password: string): Promise<{ error?: string }> => {
    setLoading(true)
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    setLoading(false)

    if (error) {
      return { error: error.message }
    }

    return {}
  }

  const logout = async (): Promise<void> => {
    setLoading(true)
    await supabase.auth.signOut()
    setLoading(false)
  }

  const updatePassword = async (
    currentPassword: string,
    newPassword: string
  ): Promise<{ error?: string }> => {
    try {
      // First verify the current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: currentPassword,
      })

      if (signInError) {
        return { error: 'Mevcut şifre yanlış' }
      }

      // Update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (updateError) {
        return { error: updateError.message }
      }

      return {}
    } catch (error) {
      return { error: 'Beklenmeyen bir hata oluştu' }
    }
  }

  return (
    <AuthContext.Provider
      value={{ user, session, loading, companyId, login, logout, updatePassword }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
